import moment from 'moment';
import LANGUAGES_CONTROLLER from '../controllers/languages';
import AppConfig from './appConfig';

// -------------------------------------------------------------------------
// File Status Column Helpers
// -------------------------------------------------------------------------

export const ProcessingStatusMap = Object.freeze({
    Unknown: 'uploadedDocuments_fileStatus_Unknown',
    Processed: 'uploadedDocuments_fileStatus_Processed',
    Failed: 'uploadedDocuments_fileStatus_Failed',
    Processing: 'uploadedDocuments_fileStatus_Processing',
    PreProcessing: 'uploadedDocuments_fileStatus_PreProcessing'
});

export const ProcessingStatusColorMap = Object.freeze({
    Unknown: 'gray-lighter',
    Processed: 'green-lighter',
    Failed: 'red-lighter',
    Processing: 'blue-lighter',
    PreProcessing: 'blue-lighter'
});

// Statuses that cannot be selected for export, delete, or redeliver operations
export const NON_ACTIONABLE_STATUSES = new Set(['Unknown', 'Failed', 'PreProcessing']);

export const resolveStatusKey = (processingStatus) => {
    const key = String(processingStatus);
    return key in ProcessingStatusMap ? key : 'Unknown';
};

export const getProcessingStatusLabel = (languages, statusKey) => {
    const labelStringKey = ProcessingStatusMap[statusKey];
    return labelStringKey ? LANGUAGES_CONTROLLER.format(languages, labelStringKey) : undefined;
};

// -------------------------------------------------------------------------
// Invoice Status column helpers
// -------------------------------------------------------------------------

export const InvoiceStatusMap = Object.freeze({
    Unknown: 'uploadedDocuments_invoiceStatus_Unknown',
    NotAvailable: 'uploadedDocuments_invoiceStatus_NotAvailable',
    Pending: 'uploadedDocuments_invoiceStatus_Pending',
    PartialSuccess: 'uploadedDocuments_invoiceStatus_PartialSuccess',
    Error: 'uploadedDocuments_invoiceStatus_Error',
    Success: 'uploadedDocuments_invoiceStatus_Success',
    Processing: 'uploadedDocuments_invoiceStatus_Processing'
});

export const InvoiceStatusColorMap = Object.freeze({
    Unknown: 'gray-lighter',
    NotAvailable: 'gray-lighter',
    Pending: 'gray-lighter',
    PartialSuccess: 'yellow-light',
    Error: 'red-lighter',
    Success: 'green-lighter',
    Processing: 'blue-lighter'
});

// Invoice statuses that have valid count values
export const COUNTABLE_INVOICE_STATUSES = new Set(['PartialSuccess', 'Error', 'Success', 'Processing']);

export const resolveInvoiceStatusKey = (invoiceStatus) => {
    const key = String(invoiceStatus);
    return key in InvoiceStatusMap ? key : 'Unknown';
};

export const getInvoiceStatusLabel = (languages, statusKey) => {
    const labelStringKey = InvoiceStatusMap[statusKey];
    return labelStringKey ? LANGUAGES_CONTROLLER.format(languages, labelStringKey) : undefined;
};

export const getDocumentCounts = (document) => {
    const source = document || {};
    const processed = source.processedCount || 0;
    const error = source.errorCount || 0;
    const onhold = source.onholdCount || 0;
    const inbox = source.inboxCount || 0;
    return { processed, error, onhold, inbox, total: processed + error + onhold + inbox };
};

// -------------------------------------------------------------------------
// Details Row Helpers
// -------------------------------------------------------------------------

export const DetailsRowContentType = Object.freeze({
    ChildRows: 'ChildRows',
    FailureDetails: 'FailureDetails',
    None: 'None'
});

export const resolveDetailsRowContentType = (document) => {
    const statusKey = resolveStatusKey(document.processingStatus);
    const hasChildRows = document.childRows && document.childRows.length > 0;

    switch (statusKey) {
        case 'Failed':
            return DetailsRowContentType.FailureDetails;
        case 'Unknown':
        case 'Processed':
        case 'Processing':
        case 'PreProcessing':
            return hasChildRows ? DetailsRowContentType.ChildRows : DetailsRowContentType.None;
        default:
            return DetailsRowContentType.None;
    }
};

// -------------------------------------------------------------------------
// Filter Panel Helpers
// -------------------------------------------------------------------------

// Order shown in the status filter dropdown
export const FILTERABLE_PROCESSING_STATUSES = ['Failed', 'Processed', 'Processing', 'PreProcessing'];


// Used when neither the URL nor the stored global filter supplu the date range
export function getDefaultDateRange() {
    const today = new Date();
    return {
        startDate: moment.utc(today).startOf('month').format(AppConfig.DateTimeConfig.DEFAULT_MOMENT_DATE_FORMAT),
        endDate: moment.utc(today).endOf('month').format(AppConfig.DateTimeConfig.DEFAULT_MOMENT_DATE_FORMAT)
    };
}

// Drops Unknown status so a hand-edited URL cannot reach the API.
export function normalizeStatusFilter(raw) {
    const requested = [].concat(raw || []).filter((value) => typeof value === 'string');
    return FILTERABLE_PROCESSING_STATUSES.filter((key) => requested.includes(key));
}

export function isInvalidDateRange(startDate, endDate) {
    const format = AppConfig.DateTimeConfig.DEFAULT_MOMENT_DATE_FORMAT;
    const start = moment.utc(startDate, format, true);
    const end = moment.utc(endDate, format, true);
    return start.isValid() && end.isValid() && start.isAfter(end);
}
